import type { StudentDashboardData } from '@services/student/studentQueries';

export interface JourneyNavItem {
  key: string;
  label: string;
  route: string;
  /** lucide icon name resolved via ModuleTemplatesPage/iconHelper. */
  icon: string;
  /** Show a "soon" affordance and treat as non-navigable target. */
  soon?: boolean;
  /** Include in the mobile bottom tab bar (max 5 across the whole set). */
  primary?: boolean;
}

/**
 * How a CLASSIC `sidebar_modules` slug maps into the Journey information
 * architecture. Order from `sidebar_modules` is preserved; unknown slugs pass
 * through with their original route so an admin-configured custom module still
 * appears.
 */
const SLUG_MAP: Record<string, Partial<JourneyNavItem> & { route: string; icon: string; label: string }> = {
  dashboard: { route: '/student/journey', icon: 'Compass', label: 'Journey', primary: true },
  recordings: { route: '/student/calendar', icon: 'Calendar', label: 'Calendar', primary: true },
  'coding-tasks': { route: '/student/code/1', icon: 'Code2', label: 'Coding', primary: true },
  'python-lab': { route: '/student/lab', icon: 'Terminal', label: 'Python Lab', primary: true },
  'skill-profile': { route: '/student/skill-profile', icon: 'Star', label: 'Skill Profile' },
  profile: { route: '/student/profile', icon: 'User', label: 'Profile', primary: true },
};

/** Always present, regardless of module config. */
const JOURNEY_HOME: JourneyNavItem = { key: 'journey', label: 'Journey', route: '/student/journey', icon: 'Compass', primary: true };
const ACHIEVEMENTS: JourneyNavItem = { key: 'achievements', label: 'Achievements', route: '/student/achievements', icon: 'Award' };

const FALLBACK: JourneyNavItem[] = [
  JOURNEY_HOME,
  { key: 'calendar', label: 'Calendar', route: '/student/calendar', icon: 'Calendar', primary: true },
  { key: 'coding', label: 'Coding', route: '/student/code/1', icon: 'Code2', primary: true },
  { key: 'lab', label: 'Python Lab', route: '/student/lab', icon: 'Terminal', primary: true },
  ACHIEVEMENTS,
  { key: 'skill-profile', label: 'Skill Profile', route: '/student/skill-profile', icon: 'Star' },
  { key: 'profile', label: 'Profile', route: '/student/profile', icon: 'User', primary: true },
];

export function buildJourneyNav(dashboard: StudentDashboardData | undefined): JourneyNavItem[] {
  const modules = dashboard?.sidebar_modules;
  if (!modules || modules.length === 0) return FALLBACK;

  const items: JourneyNavItem[] = [JOURNEY_HOME];
  const seen = new Set<string>(['journey']);

  for (const m of modules) {
    const slug = m.slug || m.route || '';
    if (slug === 'dashboard') continue; // already represented by Journey Home
    const mapped = SLUG_MAP[slug];
    const item: JourneyNavItem = mapped
      ? { key: slug, label: mapped.label, route: mapped.route, icon: mapped.icon, soon: mapped.soon, primary: mapped.primary }
      : { key: slug || m.title || 'mod', label: m.title || slug, route: m.route || '/student/journey', icon: m.icon || 'BookOpen' };
    if (!seen.has(item.key)) {
      items.push(item);
      seen.add(item.key);
    }
  }

  if (!seen.has('achievements')) items.splice(Math.max(1, items.length - 1), 0, ACHIEVEMENTS);
  return items;
}

/** Bounce a JOURNEY student who lands on a CLASSIC-only path to its Journey equivalent. */
export function legacyRedirect(pathname: string): string | null {
  const exact: Record<string, string> = {
    '/student/dashboard': '/student/journey',
    '/student/tasks': '/student/code/1',
    '/student/recordings': '/student/calendar',
    '/student/notebook': '/student/lab',
  };
  if (exact[pathname]) return exact[pathname];
  const week = pathname.match(/^\/student\/weeks\/(\d+)/);
  if (week) return `/student/code/${week[1]}`;
  return null;
}

/** Mobile bottom bar — the first 5 `primary` items. */
export function bottomTabItems(items: JourneyNavItem[]): JourneyNavItem[] {
  return items.filter((i) => i.primary).slice(0, 5);
}
